/**
 * Component: ExportButton
 * SpecReferenceID: ADMIN-V1
 * Requirements: ADMIN-V1-R11
 *
 * Export button component supporting CSV and JSON format exports.
 * ADMIN-V1-R11: System shall support export of COUNTER-aligned usage metrics
 *               to CSV and JSON formats.
 * Accessibility: WCAG 2.1 AA compliant with proper ARIA labels
 */

import React, { useState, useCallback } from 'react';
import { Download, ChevronDown, FileJson, FileSpreadsheet } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export type ExportFormat = 'csv' | 'json';

export interface ExportData {
  /** Filename without extension */
  filename: string;
  /** Column headers for CSV export */
  headers: string[];
  /** Data rows - each row is an object with header keys */
  rows: Record<string, string | number | boolean | null>[];
}

export interface ExportButtonProps {
  /** Data to export */
  data: ExportData;
  /** Disabled state */
  disabled?: boolean;
  /** Callback when export is triggered */
  onExport?: (format: ExportFormat) => void;
}

/**
 * Convert data to CSV string
 */
function convertToCSV(data: ExportData): string {
  const { headers, rows } = data;

  // Header row
  const headerRow = headers.map(escapeCSVValue).join(',');

  // Data rows
  const dataRows = rows.map((row) =>
    headers.map((header) => escapeCSVValue(String(row[header] ?? ''))).join(',')
  );

  return [headerRow, ...dataRows].join('\n');
}

/**
 * Escape CSV value (wrap in quotes if contains comma, quote, or newline)
 */
function escapeCSVValue(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/**
 * Convert data to JSON string
 */
function convertToJSON(data: ExportData): string {
  const { rows, headers } = data;

  // Create clean objects with only the header keys
  const cleanedRows = rows.map((row) => {
    const cleanRow: Record<string, string | number | boolean | null> = {};
    headers.forEach((header) => {
      cleanRow[header] = row[header] ?? null;
    });
    return cleanRow;
  });

  return JSON.stringify(
    {
      exportDate: new Date().toISOString(),
      recordCount: rows.length,
      data: cleanedRows,
    },
    null,
    2
  );
}

/**
 * Trigger browser download of file
 */
function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

/**
 * Export button with dropdown for format selection.
 *
 * ADMIN-V1-R11: Provides export of usage metrics to CSV and JSON formats
 * for COUNTER-aligned publisher reporting.
 */
export function ExportButton({
  data,
  disabled = false,
  onExport,
}: ExportButtonProps): React.ReactElement {
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = useCallback(
    (format: ExportFormat) => {
      setIsExporting(true);

      try {
        let content: string;
        let filename: string;
        let mimeType: string;

        if (format === 'csv') {
          content = convertToCSV(data);
          filename = `${data.filename}.csv`;
          mimeType = 'text/csv;charset=utf-8;';
        } else {
          content = convertToJSON(data);
          filename = `${data.filename}.json`;
          mimeType = 'application/json;charset=utf-8;';
        }

        downloadFile(content, filename, mimeType);
        onExport?.(format);
      } finally {
        setIsExporting(false);
      }
    },
    [data, onExport]
  );

  const hasData = data.rows.length > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled || isExporting || !hasData}
          aria-label="Export usage metrics"
        >
          <Download className="mr-2 h-4 w-4" aria-hidden="true" />
          Export
          <ChevronDown className="ml-2 h-4 w-4" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={() => handleExport('csv')}
          disabled={isExporting}
        >
          <FileSpreadsheet className="mr-2 h-4 w-4" aria-hidden="true" />
          Export as CSV
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleExport('json')}
          disabled={isExporting}
        >
          <FileJson className="mr-2 h-4 w-4" aria-hidden="true" />
          Export as JSON
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default ExportButton;
